(() => {
  'use strict';

  const STORE_SEARCH_TYPES = 'product,page,article';
  const SEARCH_SECTION_SELECTOR = '[data-search-results-section]';
  const PREDICTIVE_ROOT_SELECTOR = '[data-pw-predictive-search]';
  const MIN_QUERY_LENGTH = 2;
  const INDEX_QUERY_LENGTH = 4;
  const QUICK_RESULT_LIMIT = 10;
  const DEBOUNCE_DELAY = 160;
  const INDEX_CACHE_VERSION = 'v3';
  const INDEX_CACHE_TTL = 30 * 60 * 1000;

  let pageState = null;
  let debounceTimer = null;
  let requestId = 0;
  let abortController = null;
  let catalogIndexPromise = null;

  const normalize = (value) => String(value || '').trim().toLowerCase();

  const normalizeIdentifier = (value) =>
    String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');

  const isCompactQuery = (query) => !/\s/.test(String(query || '').trim());

  const isSearchUrl = (value) => {
    try {
      const url = new URL(value, window.location.origin);
      return url.origin === window.location.origin && url.pathname.replace(/\/$/, '') === '/search';
    } catch (error) {
      return false;
    }
  };

  const isSearchPage = () => window.location.pathname.replace(/\/$/, '') === '/search';

  const configureSearchForm = (form) => {
    if (!(form instanceof HTMLFormElement) || !isSearchUrl(form.action)) return;

    let typeField = form.querySelector('input[name="type"]');
    if (!typeField) {
      typeField = document.createElement('input');
      typeField.type = 'hidden';
      typeField.name = 'type';
      form.append(typeField);
    }
    typeField.value = STORE_SEARCH_TYPES;

    let prefixField = form.querySelector('input[name="options[prefix]"]');
    if (!prefixField) {
      prefixField = document.createElement('input');
      prefixField.type = 'hidden';
      prefixField.name = 'options[prefix]';
      form.append(prefixField);
    }
    prefixField.value = 'last';

    form.querySelectorAll('input[name="filter.v.availability"], input[name="pw_availability"]')
      .forEach((field) => field.remove());
  };

  const configureSearchLinks = (root = document) => {
    root.querySelectorAll?.('a[href*="/search?"]').forEach((link) => {
      if (!(link instanceof HTMLAnchorElement) || !isSearchUrl(link.href)) return;
      try {
        const url = new URL(link.href, window.location.origin);
        url.searchParams.set('type', STORE_SEARCH_TYPES);
        url.searchParams.set('options[prefix]', 'last');
        url.searchParams.delete('filter.v.availability');
        url.searchParams.delete('pw_availability');
        link.href = url.toString();
      } catch (error) {
        // Laat de originele link staan als de URL niet verwerkt kan worden.
      }
    });
  };

  const getSearchRoot = () => document.querySelector(PREDICTIVE_ROOT_SELECTOR);

  const getSearchInput = () => {
    const input = getSearchRoot()?.querySelector('[data-pw-search-input]');
    return input instanceof HTMLInputElement ? input : null;
  };

  const getPredictiveUrl = () => {
    const configured = getSearchRoot()?.dataset.predictiveSearchUrl || '/search/suggest';
    return configured.endsWith('.json') ? configured : `${configured}.json`;
  };

  const getSearchIndexUrl = () =>
    getSearchRoot()?.dataset.searchIndexUrl || '/collections/all?view=pw-search-index';

  const createFullSearchUrl = (query) => {
    const root = getSearchRoot();
    const configured = root?.dataset.searchUrl || '/search';
    const url = new URL(configured, window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('type', STORE_SEARCH_TYPES);
    url.searchParams.set('options[prefix]', 'last');
    return url.toString();
  };

  const updateHeading = (query, quick = false) => {
    const text = pageState?.section?.querySelector('.pw-collection-header p');
    if (!text) return;

    if (!query) {
      text.textContent = 'Gebruik de zoekbalk bovenaan om te zoeken.';
      return;
    }

    text.textContent = quick
      ? `Snelle resultaten voor “${query}”`
      : `Resultaten voor “${query}”`;
  };

  const updateCount = (count, quick = false) => {
    const label = pageState?.section?.querySelector('.pw-results-toolbar__count');
    if (!label) return;

    if (quick) {
      label.textContent = `${count} ${count === 1 ? 'snel resultaat' : 'snelle resultaten'}`;
      return;
    }

    label.textContent = `${count} ${count === 1 ? 'resultaat' : 'resultaten'}`;
  };

  const setQuickMode = (enabled) => {
    pageState?.section?.classList.toggle('is-quick-search', Boolean(enabled));
  };

  const getImageUrl = (image) => {
    if (!image) return '';
    if (typeof image === 'string') return image;
    return image.url || image.src || '';
  };

  const getProductUrl = (product) => product.url || (product.handle ? `/products/${product.handle}` : '#');

  const createQuickRow = (product) => {
    const item = document.createElement('li');
    item.className = 'pw-search-quick__item';

    const link = document.createElement('a');
    link.className = 'pw-search-quick__link';
    link.href = getProductUrl(product);

    const media = document.createElement('span');
    media.className = 'pw-search-quick__media';

    const imageUrl = getImageUrl(product.image || product.featured_image);
    if (imageUrl) {
      const image = document.createElement('img');
      image.src = imageUrl;
      image.alt = '';
      image.loading = 'lazy';
      image.decoding = 'async';
      media.append(image);
    } else {
      const placeholder = document.createElement('span');
      placeholder.className = 'pw-search-quick__placeholder';
      placeholder.setAttribute('aria-hidden', 'true');
      media.append(placeholder);
    }

    const text = document.createElement('span');
    text.className = 'pw-search-quick__text';

    if (product.vendor) {
      const vendor = document.createElement('span');
      vendor.className = 'pw-search-quick__vendor';
      vendor.textContent = product.vendor;
      text.append(vendor);
    }

    const title = document.createElement('strong');
    title.className = 'pw-search-quick__title';
    title.textContent = product.title || 'Product bekijken';
    text.append(title);

    const arrow = document.createElement('span');
    arrow.className = 'pw-search-quick__arrow';
    arrow.setAttribute('aria-hidden', 'true');
    arrow.textContent = '→';

    link.append(media, text, arrow);
    item.append(link);
    return item;
  };

  const createMoreLink = (query) => {
    const link = document.createElement('a');
    link.className = 'pw-search-quick__more';
    link.href = createFullSearchUrl(query);
    link.textContent = 'Toon meer resultaten';
    return link;
  };

  const renderQuickProducts = (query, products) => {
    if (!pageState?.resultsGrid?.isConnected) return;

    const group = document.createElement('div');
    group.className = 'pw-search-quick';
    group.setAttribute('aria-live', 'polite');

    const list = document.createElement('ul');
    list.className = 'pw-search-quick__list';
    list.setAttribute('aria-label', 'Snelle zoekresultaten');

    products.slice(0, QUICK_RESULT_LIMIT).forEach((product) => list.append(createQuickRow(product)));
    group.append(list, createMoreLink(query));

    pageState.resultsGrid.replaceChildren(group);
    setQuickMode(true);
    updateHeading(query, true);
    updateCount(Math.min(products.length, QUICK_RESULT_LIMIT), true);
    pageState.mode = 'quick';
  };

  const renderQuickStatus = (query, message, includeMoreLink = false) => {
    if (!pageState?.resultsGrid?.isConnected) return;

    const group = document.createElement('div');
    group.className = 'pw-search-quick pw-search-quick--status';
    group.setAttribute('aria-live', 'polite');

    const status = document.createElement('div');
    status.className = 'pw-search-live-status';
    status.setAttribute('role', 'status');
    status.textContent = message;
    group.append(status);

    if (includeMoreLink && query.length >= MIN_QUERY_LENGTH) {
      group.append(createMoreLink(query));
    }

    pageState.resultsGrid.replaceChildren(group);
    setQuickMode(true);
    updateHeading(query, true);
    updateCount(0, true);
    pageState.mode = 'quick';
  };

  const restoreServerResults = () => {
    if (!pageState?.resultsGrid?.isConnected) return;

    pageState.resultsGrid.innerHTML = pageState.serverMarkup;
    const label = pageState.section.querySelector('.pw-results-toolbar__count');
    if (label) label.textContent = pageState.serverCountLabel;
    updateHeading(pageState.initialQuery, false);
    setQuickMode(false);
    pageState.mode = 'server';
  };

  const getQueryCandidates = (query) => {
    const candidates = [query];
    const hasLetters = /[A-Za-z]/.test(query);
    const hasNumbers = /\d/.test(query);

    if (hasLetters && hasNumbers) {
      const collapsed = query.replace(/[\s._\-/]+/g, '');
      if (collapsed !== query && collapsed.length >= MIN_QUERY_LENGTH) {
        candidates.push(collapsed);
      } else {
        const split = query
          .replace(/([A-Za-z])(\d)/g, '$1 $2')
          .replace(/(\d)([A-Za-z])/g, '$1 $2')
          .replace(/\s+/g, ' ')
          .trim();
        if (split !== query && split.length >= MIN_QUERY_LENGTH) candidates.push(split);
      }
    }

    return [...new Set(candidates)].slice(0, 2);
  };

  const fetchProducts = async (query, signal) => {
    const url = new URL(getPredictiveUrl(), window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', String(QUICK_RESULT_LIMIT));
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][unavailable_products]', 'last');
    url.searchParams.set(
      'resources[options][fields]',
      'title,vendor,product_type,tag,variants.title,variants.sku,variants.barcode'
    );

    const response = await fetch(url.toString(), {
      headers: { Accept: 'application/json' },
      signal,
      credentials: 'same-origin'
    });

    if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);
    const payload = await response.json();
    return payload?.resources?.results?.products || [];
  };

  const mergeProducts = (sets, limit = Number.POSITIVE_INFINITY) => {
    const seen = new Set();
    const merged = [];

    sets.flat().forEach((product) => {
      if (merged.length >= limit) return;

      const rawKey = getProductUrl(product) || product.id || product.title || '';
      const key = String(rawKey).split('?')[0].replace(/\/$/, '').toLowerCase();
      if (!key || seen.has(key)) return;

      seen.add(key);
      merged.push(product);
    });

    return merged;
  };

  const shouldUseCatalogIndex = (query, useAsFallback = false) => {
    const normalized = normalizeIdentifier(query);
    if (normalized.length < INDEX_QUERY_LENGTH || !isCompactQuery(query)) return false;

    const hasNumber = /\d/.test(normalized);
    const hasLetter = /[a-z]/.test(normalized);
    return hasNumber || (useAsFallback && hasLetter);
  };

  const getCatalogCacheKey = (baseUrl) => `pw-search-index:${INDEX_CACHE_VERSION}:${baseUrl}`;

  const fetchCatalogIndexPage = async (baseUrl, page) => {
    const url = new URL(baseUrl, window.location.origin);
    url.searchParams.set('page', String(page));

    const previewThemeId = new URLSearchParams(window.location.search).get('preview_theme_id');
    if (previewThemeId) url.searchParams.set('preview_theme_id', previewThemeId);

    const response = await fetch(url.toString(), {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    });

    if (!response.ok) throw new Error(`Search index failed: ${response.status}`);

    const payload = await response.json();
    if (!payload || !Array.isArray(payload.products)) {
      throw new Error('Search index returned invalid data');
    }

    return payload;
  };

  const fetchCatalogIndex = async (baseUrl) => {
    const firstPage = await fetchCatalogIndexPage(baseUrl, 1);
    const pageCount = Math.max(1, Number(firstPage.pages || 1));
    const remainingRequests = [];

    for (let page = 2; page <= pageCount; page += 1) {
      remainingRequests.push(fetchCatalogIndexPage(baseUrl, page));
    }

    const remainingPages = await Promise.all(remainingRequests);
    const products = [firstPage, ...remainingPages].flatMap((page) => page.products || []);

    try {
      window.sessionStorage.setItem(
        getCatalogCacheKey(baseUrl),
        JSON.stringify({ cachedAt: Date.now(), products })
      );
    } catch (error) {
      // Zoeken blijft werken wanneer browseropslag niet beschikbaar is.
    }

    return products;
  };

  const loadCatalogIndex = async () => {
    const baseUrl = new URL(getSearchIndexUrl(), window.location.origin).toString();
    if (catalogIndexPromise) return catalogIndexPromise;

    try {
      const cached = JSON.parse(window.sessionStorage.getItem(getCatalogCacheKey(baseUrl)) || 'null');
      if (
        cached &&
        Array.isArray(cached.products) &&
        Date.now() - Number(cached.cachedAt || 0) <= INDEX_CACHE_TTL
      ) {
        catalogIndexPromise = Promise.resolve(cached.products);
        return catalogIndexPromise;
      }
    } catch (error) {
      // Ga verder met ophalen.
    }

    catalogIndexPromise = fetchCatalogIndex(baseUrl).catch((error) => {
      catalogIndexPromise = null;
      throw error;
    });

    return catalogIndexPromise;
  };

  const getBestIdentifierScore = (values, needle, offset) => {
    let best = Number.POSITIVE_INFINITY;

    values.forEach((value) => {
      const haystack = normalizeIdentifier(value);
      if (!haystack) return;

      if (haystack === needle) {
        best = Math.min(best, offset);
        return;
      }

      if (haystack.startsWith(needle)) {
        best = Math.min(best, offset + 100 + haystack.length - needle.length);
        return;
      }

      const matchIndex = haystack.indexOf(needle);
      if (matchIndex !== -1) {
        best = Math.min(best, offset + 200 + matchIndex);
      }
    });

    return best;
  };

  const findCatalogMatches = (products, query) => {
    const needle = normalizeIdentifier(query);
    if (!needle) return [];

    return products
      .filter((product) => !product.hidden)
      .map((product) => {
        const identifierScore = getBestIdentifierScore(
          [...(product.skus || []), ...(product.barcodes || [])],
          needle,
          0
        );
        const titleScore = getBestIdentifierScore([product.title], needle, 1000);
        const score = Math.min(identifierScore, titleScore);

        if (!Number.isFinite(score)) return null;

        return {
          ...product,
          id: `catalog-${product.id}`,
          image: product.image ? { url: product.image } : null,
          _searchScore: score
        };
      })
      .filter(Boolean)
      .sort((a, b) => {
        if (a._searchScore !== b._searchScore) return a._searchScore - b._searchScore;
        if (Boolean(a.available) !== Boolean(b.available)) return a.available ? -1 : 1;
        return String(a.title || '').localeCompare(String(b.title || ''), 'nl');
      });
  };

  const runQuickSearch = async (query) => {
    if (!pageState?.resultsGrid?.isConnected) return;
    const currentRequest = ++requestId;

    abortController?.abort();
    abortController = new AbortController();
    const { signal } = abortController;

    renderQuickStatus(query, 'Zoeken…');

    let nativeProducts = [];

    try {
      const candidates = getQueryCandidates(query);
      const sets = await Promise.all(candidates.map((candidate) => fetchProducts(candidate, signal)));
      nativeProducts = mergeProducts(sets, QUICK_RESULT_LIMIT);
    } catch (error) {
      if (error.name === 'AbortError') return;
    }

    if (currentRequest !== requestId || signal.aborted) return;

    const input = getSearchInput();
    if (!input || normalize(input.value) !== normalize(query)) return;

    if (nativeProducts.length > 0) {
      renderQuickProducts(query, nativeProducts);
    }

    const useIndex = shouldUseCatalogIndex(query, nativeProducts.length === 0);
    if (!useIndex) {
      if (nativeProducts.length === 0) {
        renderQuickStatus(
          query,
          'Geen snelle resultaten gevonden. Gebruik “Toon meer resultaten” voor de volledige zoekopdracht.',
          true
        );
      }
      return;
    }

    if (nativeProducts.length === 0) {
      renderQuickStatus(query, 'Typenummer controleren…');
    }

    try {
      const index = await loadCatalogIndex();
      if (currentRequest !== requestId) return;

      const currentInput = getSearchInput();
      if (!currentInput || normalize(currentInput.value) !== normalize(query)) return;

      const indexedProducts = findCatalogMatches(index, query);
      const products = mergeProducts([indexedProducts, nativeProducts], QUICK_RESULT_LIMIT);

      if (products.length > 0) {
        renderQuickProducts(query, products);
      } else {
        renderQuickStatus(
          query,
          'Geen snelle resultaten gevonden. Gebruik “Toon meer resultaten” voor de volledige zoekopdracht.',
          true
        );
      }
    } catch (error) {
      if (nativeProducts.length === 0) {
        renderQuickStatus(
          query,
          'Geen snelle resultaten gevonden. Gebruik “Toon meer resultaten” voor de volledige zoekopdracht.',
          true
        );
      }
    }
  };

  const scheduleQuickSearch = (query) => {
    window.clearTimeout(debounceTimer);
    requestId += 1;

    if (!pageState?.resultsGrid?.isConnected) return;

    if (normalize(query) === normalize(pageState.initialQuery)) {
      abortController?.abort();
      restoreServerResults();
      return;
    }

    if (query.length < MIN_QUERY_LENGTH) {
      abortController?.abort();
      renderQuickStatus(query, 'Vul minimaal 2 tekens in om te zoeken.');
      return;
    }

    debounceTimer = window.setTimeout(() => runQuickSearch(query), DEBOUNCE_DELAY);
  };

  const createFullProductCard = (product) => {
    const card = document.createElement('article');
    card.className = 'pw-search-product-card';

    const link = document.createElement('a');
    link.href = getProductUrl(product);

    const media = document.createElement('span');
    media.className = 'pw-search-product-card__media';

    const imageUrl = getImageUrl(product.image);
    if (imageUrl) {
      const image = document.createElement('img');
      image.src = imageUrl;
      image.alt = '';
      image.loading = 'lazy';
      image.decoding = 'async';
      media.append(image);
    }

    const body = document.createElement('span');
    body.className = 'pw-search-product-card__body';

    if (product.vendor) {
      const vendor = document.createElement('span');
      vendor.className = 'pw-search-product-card__vendor';
      vendor.textContent = product.vendor;
      body.append(vendor);
    }

    const title = document.createElement('strong');
    title.className = 'pw-search-product-card__title';
    title.textContent = product.title || 'Product bekijken';
    body.append(title);

    if (product.price) {
      const price = document.createElement('span');
      price.className = 'pw-search-product-card__price';
      price.textContent = product.price;

      if (product.compareAtPrice) {
        const compare = document.createElement('del');
        compare.textContent = product.compareAtPrice;
        price.append(compare);
      }

      body.append(price);
    }

    link.append(media, body);
    card.append(link);
    return card;
  };

  const hasSearchRefinements = () => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('sort_by') || params.has('page')) return true;

    return [...params.keys()].some((key) => key.startsWith('filter.'));
  };

  const supplementInitialServerResults = async () => {
    if (!pageState?.resultsGrid?.isConnected) return;
    if (!pageState.initialQuery || pageState.initialQuery.length < INDEX_QUERY_LENGTH) return;
    if (!isCompactQuery(pageState.initialQuery) || hasSearchRefinements()) return;
    if (pageState.serverProductCount >= QUICK_RESULT_LIMIT) return;

    try {
      const index = await loadCatalogIndex();
      if (!pageState?.resultsGrid?.isConnected || pageState.mode !== 'server') return;

      const matches = findCatalogMatches(index, pageState.initialQuery);
      if (matches.length <= pageState.serverProductCount) return;

      const existingUrls = new Set(
        [...pageState.resultsGrid.querySelectorAll('.pw-search-product-card > a[href]')]
          .map((link) => {
            try {
              return new URL(link.href, window.location.origin).pathname.replace(/\/$/, '').toLowerCase();
            } catch (error) {
              return link.getAttribute('href') || '';
            }
          })
      );

      const missing = matches.filter((product) => {
        try {
          const path = new URL(getProductUrl(product), window.location.origin).pathname.replace(/\/$/, '').toLowerCase();
          return path && !existingUrls.has(path);
        } catch (error) {
          return false;
        }
      });

      if (missing.length === 0) return;

      let grid = pageState.resultsGrid.querySelector('.pw-search-mixed-grid');

      if (!grid) {
        const group = document.createElement('div');
        group.className = 'pw-search-results-group';

        grid = document.createElement('div');
        grid.className = 'pw-search-mixed-grid';

        group.append(grid);
        pageState.resultsGrid.replaceChildren(group);
      }

      missing.forEach((product) => grid.append(createFullProductCard(product)));

      const visibleCount =
        pageState.resultsGrid.querySelectorAll('.pw-search-product-card').length +
        pageState.resultsGrid.querySelectorAll('.pw-search-content-card').length;

      updateCount(visibleCount, false);
    } catch (error) {
      // De serverresultaten blijven leidend wanneer de aanvullende typenummer-index niet beschikbaar is.
    }
  };

  const bindInput = () => {
    const input = getSearchInput();
    if (!input) return;

    if (input.dataset.pwPageSearchBound !== 'true') {
      input.dataset.pwPageSearchBound = 'true';
      input.addEventListener('input', () => scheduleQuickSearch(input.value.trim()));
    }
  };

  const initializeSearchPage = () => {
    if (!isSearchPage()) return;

    const section = document.querySelector(SEARCH_SECTION_SELECTOR);
    const resultsGrid = section?.querySelector('[data-results-grid]');
    if (!(section instanceof Element) || !(resultsGrid instanceof Element)) return;

    if (pageState?.section === section && section.dataset.pwPageSearchReady === 'true') {
      bindInput();
      return;
    }

    section.dataset.pwPageSearchReady = 'true';

    const initialQuery = String(new URLSearchParams(window.location.search).get('q') || '').trim();
    const countLabel = section.querySelector('.pw-results-toolbar__count');

    pageState = {
      section,
      resultsGrid,
      initialQuery,
      serverMarkup: resultsGrid.innerHTML,
      serverCountLabel: countLabel?.textContent?.trim() || '',
      serverProductCount: resultsGrid.querySelectorAll('.pw-search-product-card').length,
      mode: 'server'
    };

    setQuickMode(false);
    bindInput();
    window.setTimeout(() => supplementInitialServerResults(), 0);
  };

  const start = () => {
    document.querySelectorAll('form[action]').forEach(configureSearchForm);
    configureSearchLinks(document);
    initializeSearchPage();

    const observer = new MutationObserver((records) => {
      let searchSectionChanged = false;

      records.forEach((record) => {
        record.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;

          if (node instanceof HTMLFormElement) configureSearchForm(node);
          node.querySelectorAll?.('form[action]').forEach(configureSearchForm);
          configureSearchLinks(node);

          if (node.matches?.(SEARCH_SECTION_SELECTOR) || node.querySelector?.(SEARCH_SECTION_SELECTOR)) {
            searchSectionChanged = true;
          }
        });
      });

      if (searchSectionChanged) initializeSearchPage();
    });

    observer.observe(document.documentElement, { childList: true, subtree: true });

    document.addEventListener('submit', (event) => {
      configureSearchForm(event.target);
    }, true);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
